'use client';
import React, {
  useState,
  useMemo,
  useEffect,
  Suspense,
} from 'react';
import AppHeader from '@/components/AppHeader';
import ProtectedRoute from '@/components/ProtectedRoute';
import DatePicker from '@/core/components/DateTimePicker/SingleDatePicker';
import dayjs from 'utils/dayjs';
import clsx from 'clsx';
import {
  useCreateOrUpdateUserSettings,
  useGetSurgicalChecklistFilterSettings,
  useGetSurgicalChecklistsInfinite,
} from '@/hooks/useApi';
import {
  useAuth,
} from '@/contexts/AuthContext';
import {
  SurgicalChecklistsRequestDTO,
  SurgicalChecklistStatuses,
  UserSettingDTO,
} from '@/dtos';
import MultiSelect from '@/core/components/Multiselect';
import {
  getProviderOptionsFromSettings,
  getStatusOptionsFromSettings,
} from '@/utils/settings';
import compact from 'lodash/compact';
import Table from '@/core/components/VirtualTable';
import {
  SurgicalChecklistDTO,
} from '@/dtos/surgicalChecklistDTO';
import {
  columns,
} from '@/components/schedule/columns';
import {
  formatISODateTime,
} from '@/utils/date';
import {
  useMedia,
} from '@/core/hooks';
import uniq from 'lodash/uniq';
import useUrlSyncedDate from '@/hooks/useUrlSyncedDate';
import Spinner from '@/components/Spinner';
import {
  useUserSettings,
} from '@/contexts/UserSettingsContext';
import Image from 'next/image';
import {
  ArrowUp,
} from 'lucide-react';
import Button from '@/components/Button';
import isNil from 'lodash/isNil';
import {
  formatDateOnly,
} from '@/utils/date';
import {
  writeDashboardDate,
} from '@/utils/storage';
import head from 'lodash/head';
import useDebounce from '@/hooks/useDebounce';
import debounce from 'lodash/debounce';

const PAGE_SIZE = 10;

function DashboardPage() {
  const { requestMedia } = useMedia();
  const { selectedDate, setSelectedDate } = useUrlSyncedDate('date', dayjs());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilters, setStatusFilters] = useState<string[]>(['All']);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [showBackTop, setShowBackTop] = useState<boolean>(false);
  const { isAuthenticated } = useAuth();
  const { mutateAsync: upsertSettings } = useCreateOrUpdateUserSettings();
  const {
    providerIds,
    setProviderIds,
    settings: UserSettings,
  } = useUserSettings();
  const {
    data: filterSettings,
    isLoading: isFilterSettingsLoading,
  } = useGetSurgicalChecklistFilterSettings(isAuthenticated);
  // Derive doctor/provider options from settings
  const providerOptions = useMemo(
    () => getProviderOptionsFromSettings(filterSettings),
    [filterSettings],
  );

  // Derive status options from settings (use display label from API)
  const statusOptions = useMemo(
    () => getStatusOptionsFromSettings(filterSettings),
    [filterSettings],
  );

  const allStatusOptions = [{ label: 'All', value: 'All' }, ...statusOptions];

  const debouncedProviderIds = useDebounce<string[]>(providerIds, 500);

  const basePayload: SurgicalChecklistsRequestDTO = {
    dateRange: { start: formatISODateTime(selectedDate.startOf('day')), end: formatISODateTime(selectedDate.endOf('day')) },
    patientSearchText: searchQuery ?? undefined,
    filterQuery: {
      surgicalChecklistStatusList: (statusFilters.includes('All') || statusFilters.length === 0)
        ? null
        : statusFilters as SurgicalChecklistStatuses[],
      providerIdList: compact(debouncedProviderIds).map(Number) ?? null,
    },
    pageRequest: { page: 0, pageSize: PAGE_SIZE },
  };

  const {
    data: pagedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetSurgicalChecklistsInfinite(
    basePayload,
    isAuthenticated,
  );

  const surgicalChecklists = useMemo<SurgicalChecklistDTO[]>(
    () => (pagedData?.pages ?? []).flatMap((p) => (p?.data as SurgicalChecklistDTO[] | undefined) ?? []),
    [pagedData],
  );
  const totalPatients = useMemo(
    () => head(pagedData?.pages)?.pageProperties?.totalSize ?? 0,
    [pagedData],
  );
  const isTableLoading = (isLoading && (surgicalChecklists?.length ?? 0) === 0);

  // Prefetch patient profile images for current table data
  const patientIds = useMemo(() => {
    const ids = (surgicalChecklists ?? [])
      .map((item) => item?.appointment?.patient?.patientId);
    return uniq(compact(ids));
  }, [surgicalChecklists]);

  useEffect(() => {
    if (patientIds.length > 0) {
      requestMedia(patientIds);
    }
  }, [patientIds, requestMedia]);

  const handler = () => {
    if(!isNil(scrollEl)) {
      const { scrollTop, clientHeight } = scrollEl;
      setShowBackTop(scrollTop >= clientHeight);
    }
  };

  // Show Back to Top after scrolling >= 1 viewport height of the table's scroll area
  useEffect(() => {
    scrollEl?.addEventListener('scroll', handler, { passive: true });
    return () => {
      scrollEl?.removeEventListener('scroll', handler);
    };
  }, [scrollEl]);
  // Persist selected date to localStorage (YYYY-MM-DD)
  useEffect(() => {
    try {
      const value = formatDateOnly(selectedDate);
      writeDashboardDate(value);
    } catch {
      // ignore
    }
  }, [selectedDate]);

  const handleStatusFilterChange = (item: string) => {
    const all = 'All';
    setStatusFilters([item === all ? all : item]);
  };

  const saveProviderIds = async (ids: string[]) => {
    try {
      const prevPrefJson = UserSettings?.preferenceJson ?? '{}';
      const prevPref = JSON.parse(prevPrefJson) as Record<string, unknown>;
      const nextPref = { ...prevPref, selectedProviderIds: ids };
      const dto: UserSettingDTO = {
        userSettingId: UserSettings?.userSettingId,
        preferenceJson: JSON.stringify(nextPref),
      };
      await upsertSettings(dto);
    } catch {
      // ignore
    }
  };

  const debouncedSaveProviderIds = useMemo(
    () =>
      debounce((ids: string[]) => {
        saveProviderIds(ids);
      }, 800),
    [],
  );

  return (
    <ProtectedRoute>
      <div className="h-screen flex flex-col">
        <AppHeader classes={{ root: '!px-5' }} />
        <main className="container-width flex flex-1 flex-col gap-4 mx-auto px-4 pt-4 overflow-hidden">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="w-full xl:w-auto flex flex-wrap items-center gap-3">
              <input
                placeholder="Search by patient name..."
                className="h-10 sm:w-60 min-w-[12rem] rounded-md border border-gray-200 px-3 text-sm focus:outline-none"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
              <div className="w-full sm:w-auto flex-none">
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  showDateButtons
                  accumulationUnit="day"
                  containerClassName="bg-white"
                />
              </div>
              <div className="min-w-[14rem] w-full sm:w-auto flex-none">
                {!isFilterSettingsLoading && (
                  <MultiSelect
                    id="provider-filter"
                    includeAll
                    allCaption="All Doctors"
                    selectAllCaption="Select All"
                    placeholder="Select Doctor(s)"
                    value={providerIds}
                    options={providerOptions}
                    getItemKey={(option) => option.value}
                    getItemLabel={(option) => option.label}
                    onChange={(next) => {
                      setProviderIds(next);
                      debouncedSaveProviderIds(next);
                    }}
                    hasClear
                    className='lg:min-w-[242px] w-full'
                  />)}
              </div>
            </div>
            <div className="w-full xl:flex-1 flex flex-wrap items-center gap-2 min-w-0 justify-start">
              {!isFilterSettingsLoading && allStatusOptions?.map((opt) => (
                <button
                  key={opt?.value}
                  className={clsx(
                    'h-9 px-4 !py-0.5 rounded-full border text-xs font-semibold',
                    { 'bg-emerald-100 text-emerald-700 border-emerald-200': statusFilters.includes(opt.value) },
                    { 'border-gray-200 text-slate-700 hover:bg-slate-50': !statusFilters.includes(opt.value) },
                  )}
                  onClick={() => {
                    handleStatusFilterChange(opt.value);
                  }}
                >{opt.label}</button>
              ))}
            </div>
          </div>
          <Table<SurgicalChecklistDTO>
            className="flex-1"
            data={surgicalChecklists}
            columns={columns}
            loading={isTableLoading}
            tableMeta={{ totalPatients }}
            skeleton={{ rows: 3 }}
            classes={{
              outerHeader: '!border !border-gray-200 !rounded-lg !bg-surface !cursor-default !px-4',
              header: '!text-primary !font-semibold !border-none !cursor-default !items-end',
              outerRow: '!border !border-gray-200 !rounded-lg !my-3 !py-3 bg-white hover:bg-slate-50/50 !px-4',
              row: '!cursor-default !items-center before:!bg-transparent',
              cell: '!cursor-default !items-center',
            }}
            emptySlot={<div className="px-4 py-8 text-center text-slate-500">
                No surgical procedures found.
            </div>}
            onEndReached={() => { if (hasNextPage && !isFetchingNextPage) { fetchNextPage(); } }}
            endReachedOffset={400}
            onScrollElementRef={setScrollEl}
            endTriggerOnceUntilDataChange
            itemSize={88}
            overscan={12}
          />
          {isFetchingNextPage && (
            <div className="flex justify-center py-3">
              <Button
                type="button"
                variant="outline"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-gray-200 shadow-sm text-slate-700"
                disabled
              >
                <Spinner size={16} />
                <span className="text-sm">Loading…</span>
              </Button>
            </div>
          )}
        </main>
        <div className="bg-white border-t border-gray-200 w-full p-4">
          <div className="container-width mx-auto relative">
            <footer className="flex justify-center items-center gap-2 py-3 text-center text-slate-700 text-xs">
              <span>Powered by</span>
              <Image src="/populate-logo.png" alt="Populate" width={90} height={18} />
            </footer>
            {showBackTop && (
              <Button
                type="button"
                aria-label="Back to Top"
                variant="text"
                className="top-2 absolute bottom-6 right-4 inline-flex items-center !gap-2 px-3 h-6 text-slate-900"
                onClick={() => { scrollEl?.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                <span className="text-sm">Back to Top</span>
                <ArrowUp className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

export default function DashboardSuspense() {
  return (
    <Suspense
      fallback={(
        <div className="h-screen w-screen flex items-center justify-center">
          <Spinner size={32} />
        </div>
      )}
    >
      <DashboardPage />
    </Suspense>
  );
}
